/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { SummaryResource } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper } from "@octopusdeploy/octopus-server-client";
import type * as H from "history";
import * as React from "react";
import type { RouteComponentProps } from "react-router";
import ActionList from "~/components/ActionList/ActionList";
import type { FilterSection } from "~/components/AdvancedFilterLayout";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import AdvancedFilterLayout from "~/components/AdvancedFilterLayout/AdvancedFilterLayout";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import PaperLayout from "~/components/PaperLayout/PaperLayout";
import type { IQuery } from "~/components/QueryStringFilters/QueryStringFilters";
import { convertQueryToQueryString } from "~/components/QueryStringFilters/QueryStringFilters";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import RequestRaceConditioner from "~/utils/RequestRaceConditioner";
import InternalRedirect from "../../../../components/Navigation/InternalRedirect/InternalRedirect";
import routeLinks from "../../../../routeLinks";
import InfrastructureLayout from "../InfrastructureLayout";
import ConfirmTentacleUpgradePanel from "./ConfirmTentacleUpgradePanel";
import type { BaseInfrastructureFilter } from "./MachineFilter";
export interface MachinesLayoutProps<Filter, Query> extends RouteComponentProps {
    title: string;
    itemDescriptions: string;
    hasMachines: boolean;
    defaultFilter: Filter;
    initialFilter: Filter;
    filterToQuery: (filter: Filter) => Query;
}
export interface MachinesLayoutState<Filter> extends DataBaseComponentState {
    currentPageIndex: number; // This has a custom endpoint, so we manage our own paging implementation in List/onLoadMore.
    expanded: boolean; // Need to know if we're currently expanded so we can choose to reload when the filter changes or not.
    healthStatusFilters: string[];
    isDisabledFilter: boolean;
    filter: Filter;
    queryFilter: Filter;
    redirectToTasks: boolean;
}
export abstract class BaseMachinesLayout<Props extends MachinesLayoutProps<Filter, Query>, State extends MachinesLayoutState<Filter>, Filter extends BaseInfrastructureFilter, Query extends IQuery> extends DataBaseComponent<Props, State> {
    protected machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    protected communicationStyles = EndpointsHelper.getCommunicationStyleResources();
    protected requestRaceConditioner = new RequestRaceConditioner();
    protected commonInitialState = {
        redirectToTasks: false,
        currentPageIndex: 0,
        expanded: true,
        healthStatusFilters: [],
        isDisabledFilter: false,
        isSearching: false,
    };
    constructor(props: Props) {
        super(props);
    }
    render() {
        if (this.state.redirectToTasks) {
            return <InternalRedirect to={routeLinks.tasks.root} push={true}/>;
        }
        const actionSection = <ActionList actions={this.getActions()}/>;
        const searchHintText = "Search " + this.props.itemDescriptions + "...";
        const summaries = this.getSummaries();
        const tentacleUpgradesRequiredWarning = summaries && summaries.TentacleUpgradesRequired && (<ConfirmTentacleUpgradePanel doBusyTask={this.doBusyTask} calloutDescriptionElement={<p>One or more {this.props.itemDescriptions} are running old versions of the Tentacle agent and can be upgraded.</p>} onTentacleUpgradeComplete={() => {
                this.setState({ redirectToTasks: true });
            }}/>);
        return (<InfrastructureLayout {...this.props}>
                <PaperLayout title={this.props.title} busy={this.state.busy} errors={this.errors} sectionControl={actionSection}>
                    {summaries && this.props.hasMachines && this.renderPageSummary()}
                    {summaries && !this.props.hasMachines && this.renderOnboarding()}
                    {tentacleUpgradesRequiredWarning}
                    {this.props.hasMachines && (<AdvancedFilterLayout<Filter> filterSections={this.getFilterSections()} filter={this.state.filter} queryFilter={this.state.queryFilter} defaultFilter={this.props.defaultFilter} // Base doesn't decide which kind of filter is passed down
             initiallyShowFilter={this.isFiltering()} additionalHeaderFilters={this.getNameFilter(searchHintText)} onFilterReset={(filter: Filter) => {
                    this.setState({ filter }, () => {
                        this.onFilterChange();
                        const location = { ...(this.props.history as H.History), search: "" };
                        this.props.history.replace(location);
                    });
                }} renderContent={() => this.renderMachinesExpander()}/>)}
                </PaperLayout>
            </InfrastructureLayout>);
    }
    protected abstract getSummaries(): SummaryResource;
    protected abstract isFiltering(): boolean;
    protected abstract getActions(): JSX.Element[];
    protected abstract renderOnboarding(): JSX.Element;
    protected abstract renderMachinesExpander(): React.ReactNode;
    protected abstract getNameFilter(searchHintText: string): JSX.Element[];
    protected renderPageSummary(): JSX.Element {
        return <React.Fragment />;
    }
    protected getFilterSections() {
        const filters: React.ReactNode[] = [
            <AdvancedFilterCheckbox key="filterDisabled" label="Disabled only" value={this.state.filter.isDisabled} onChange={(x) => {
                    this.setFilterState({ isDisabled: x }, () => {
                        this.onFilterChange();
                    });
                }}/>,
        ];
        filters.push(this.extraFilters());
        filters.push(<MachineModelHealthStatusMultiSelect accessibleName="healthStatusFilter" key="filterStatus" items={this.machineHealthStatuses} value={this.state.filter.healthStatuses} onChange={(x) => {
                this.setFilterState({ healthStatuses: x }, () => {
                    this.onFilterChange();
                });
            }}/>);
        const filterSections: FilterSection[] = [
            {
                render: <div>{filters}</div>,
            },
        ];
        return filterSections;
    }
    protected extraFilters(): React.ReactNode[] {
        return [];
    }
    protected setFilterState<K extends keyof Filter>(state: Pick<Filter, K>, callback?: () => void) {
        this.setState((prev) => ({
            filter: { ...(prev.filter as object), ...(state as object) },
        }), callback);
    }
    protected onFilterChange() {
        const queryString = convertQueryToQueryString(this.props.filterToQuery(this.state.filter));
        if (this.props.location.search !== queryString) {
            const location = { ...this.props.history, search: queryString };
            this.props.history.replace(location);
        }
    }
    static displayName = "BaseMachinesLayout";
}
